import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { MapContainer, TileLayer, GeoJSON, Marker, Circle, Tooltip, useMap, useMapEvents } from 'react-leaflet';
import * as L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { PlaceContext, DropPoint } from '@m4a3/api-client';
import { orderfulApi } from '../../services/api-instances';
import { useDispatch, useSelector } from 'react-redux';
import { getObstacles } from '../../actions/obstacleActions';
import { selectObstacles } from '../../reducers/obstaclesReducer';
import { selectSelectedCellId } from '../../selectors/ordersSelector';
import { useAppSelector } from '../../hooks/redux';
import Obstacles from '../../views/Restaurant/components/Obstacles';

// Constants from DropPointManagement
const MARKER_STYLES = {
  VERIFIED_COLOR: '#8B5CF6',
  UNVERIFIED_COLOR: '#3B82F6', 
  DELETED_COLOR: '#6B7280',
  SELECTED_SIZE: 14, // Reduced from 24 to ~40% smaller
  DEFAULT_SIZE: 16,
};

const RISK_COLORS = {
  HIGH: '#DC2626',
  LOW: '#10B981', 
  UNKNOWN: '#FFFFFF',
  ERROR: '#F97316', // Orange for API errors
};

const MAP_CONFIG = {
  RISK_ASSESSMENT_RADIUS: 3.0,
  DEFAULT_CELL_ID: 7,
};

type RiskLevel = 'HIGH' | 'LOW' | 'UNKNOWN' | 'ERROR';

interface RiskAssessment {
  groundRisk: RiskLevel;
  buildingRisk: RiskLevel;
  overallRisk: RiskLevel;
  canSave: boolean;
  message: string;
  heightData?: number;
  buildingReason?: string;
  hasError?: boolean;
}

// Fix for default markers in React Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface PlaceDropPointMapProps {
  placeContext: PlaceContext | null;
  selectedDropPointId: string | null;
  onDropPointSelect: (dropPointId: string, lat: number, lng: number) => void;
  suggestedDropPointLatLon?: string;
  className?: string;
  height?: string;
  showLegend?: boolean;
  clickMode?: 'select' | 'add-drop-point';
  newPinLocation?: L.LatLng | null;
  onPinMove?: (newPosition: L.LatLng) => void;
  riskAssessment?: RiskAssessment | null;
  isLoadingRiskAssessment?: boolean;
  showDeletedPoints?: boolean;
}

// Component to fit map bounds when place context changes
const MapBoundsController = ({ placeContext }: { placeContext: PlaceContext | null }) => {
  const map = useMap();

  useEffect(() => {
    if (!placeContext) return;

    const dropPoints = placeContext.dropPoints?.data || [];

    if (dropPoints.length === 0 && placeContext.place.centroid) {
      // If no drop points, center on the building centroid
      map.setView([placeContext.place.centroid.latitude, placeContext.place.centroid.longitude], 18);
      return;
    }

    // Create bounds that include all drop points and the building
    const bounds = L.latLngBounds([]);
    
    // Add drop points to bounds
    dropPoints.forEach(dp => {
      if (!dp.isDeleted) {
        bounds.extend([dp.latitude, dp.longitude]);
      }
    });

    // Add building centroid to bounds
    if (placeContext.place.centroid) {
      bounds.extend([placeContext.place.centroid.latitude, placeContext.place.centroid.longitude]);
    }

    // Fit the map to show all points with some padding
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [20, 20], maxZoom: 19 });
    }
  }, [map, placeContext]);

  return null;
};

// Component to handle map clicks for pin placement
const MapClickHandler = ({ 
  clickMode, onPinMove
}: { 
  clickMode: 'select' | 'add-drop-point';
  onPinMove?: (newPosition: L.LatLng) => void;
}) => {
  useMapEvents({
    click(e) {
      if (clickMode === 'add-drop-point' && onPinMove) {
        onPinMove(e.latlng);
      }
    },
  });

  return null;
};

const PlaceDropPointMap = ({
  placeContext,
  selectedDropPointId,
  onDropPointSelect,
  suggestedDropPointLatLon,
  className = '',
  height = '24rem', // default 384px
  showLegend = true,
  clickMode = 'select',
  newPinLocation = null,
  onPinMove,
  riskAssessment: externalRiskAssessment = null,
  isLoadingRiskAssessment: externalIsLoadingRiskAssessment = false,
  showDeletedPoints = false,
}: PlaceDropPointMapProps) => {
  const [internalRiskAssessment, setInternalRiskAssessment] = useState<RiskAssessment | null>(null);
  const [internalIsLoadingRiskAssessment, setInternalIsLoadingRiskAssessment] = useState(false);

  // Redux hooks for obstacles
  const dispatch = useDispatch();
  const selectedCellId = useAppSelector(selectSelectedCellId);
  const obstacles = useSelector(selectObstacles);

  // Use external risk assessment if provided, otherwise use internal
  const riskAssessment = externalRiskAssessment || internalRiskAssessment;
  const isLoadingRiskAssessment = externalIsLoadingRiskAssessment || internalIsLoadingRiskAssessment;

  // Create icon cache to avoid recreating icons on every render
  const iconCache = useMemo(() => new Map<string, L.DivIcon>(), []);
  let suggestedDropPoint: [number, number] | null = null;
  if (suggestedDropPointLatLon) {
    suggestedDropPoint = suggestedDropPointLatLon.split(',').map(Number) as [number, number];
  }

  const createCachedDropPointIcon = useCallback((isSelected: boolean, isDeleted: boolean, isVerified: boolean, isPreferred: boolean): L.DivIcon => {
    const cacheKey = `${isSelected}-${isDeleted}-${isVerified}-${isPreferred}`;
    
    if (iconCache.has(cacheKey)) {
      return iconCache.get(cacheKey)!;
    }
    
    const size = isSelected ? MARKER_STYLES.SELECTED_SIZE : MARKER_STYLES.DEFAULT_SIZE;
    let backgroundColor = MARKER_STYLES.UNVERIFIED_COLOR; // Default blue for unverified
    
    if (isDeleted) {
      backgroundColor = MARKER_STYLES.DELETED_COLOR; // Gray for deleted
    } else if (isVerified) {
      backgroundColor = MARKER_STYLES.VERIFIED_COLOR; // Purple for verified
    } else {
      backgroundColor = MARKER_STYLES.UNVERIFIED_COLOR; // Blue for unverified
    }
    
    // Create star overlay for preferred drop points
    const starOverlay = isPreferred ? `
      <div style="
        position: absolute;
        top: -6px;
        right: -6px;
        width: 16px;
        height: 16px;
        background-color: #FFD700;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 1px 3px rgba(0,0,0,0.4);
        z-index: 10;
      ">
        <span style="color: white; font-size: 10px; font-weight: bold;">★</span>
      </div>
    ` : '';
    
    const icon = L.divIcon({
      html: `
        <div style="
          background-color: ${backgroundColor};
          border: 2px solid ${isPreferred ? '#FFD700' : 'white'};
          border-radius: 50%;
          width: ${size}px;
          height: ${size}px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
          ${isSelected ? 'transform: scale(1.2); z-index: 1000;' : ''}
          ${isDeleted ? 'opacity: 0.6;' : ''}
        "></div>
        ${starOverlay}
      `,
      className: `circular-drop-point-marker ${isSelected ? 'selected' : ''} ${isDeleted ? 'deleted' : ''} ${isPreferred ? 'preferred' : ''}`,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2]
    });
    
    iconCache.set(cacheKey, icon);
    return icon;
  }, [iconCache]);

  // Create draggable pin icon for new pin placement with ripple effect
  const createNewPinIcon = useCallback((): L.DivIcon => {
    return L.divIcon({
      html: `
        <style>
          @keyframes ripple-pulse {
            0% {
              transform: scale(1);
              opacity: 0.8;
            }
            100% {
              transform: scale(4);
              opacity: 0;
            }
          }
        </style>
        <div style="position: relative; width: 100px; height: 100px; display: flex; align-items: center; justify-content: center;">
          <div style="
            position: absolute;
            background-color: transparent;
            border: 3px solid #FCD34D;
            border-radius: 50%;
            width: 20px;
            height: 20px;
            animation: ripple-pulse 1.5s ease-out infinite;
          "></div>
          <div style="
            position: absolute;
            background-color: #F59E0B;
            border: 3px solid #FCD34D;
            border-radius: 50%;
            width: 20px;
            height: 20px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.4);
            z-index: 1;
          "></div>
        </div>
      `,
      className: 'new-drop-point-marker',
      iconSize: [100, 100], // Large container for ripple expansion (4x of 20px + padding)
      iconAnchor: [50, 50] // Center of container = center of yellow dot
    });
  }, []);

  // Create grey static icon for suggested drop point
  const createSuggestedPinIcon = useCallback((): L.DivIcon => {
    return L.divIcon({
      html: `
        <div style="
          background-color: #6B7280;
          border: 3px solid #9CA3AF;
          border-radius: 50%;
          width: 20px;
          height: 20px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.4);
        "></div>
      `,
      className: 'suggested-drop-point-marker',
      iconSize: [20, 20],
      iconAnchor: [10, 10]
    });
  }, []);

  // Risk evaluation function
  const performRiskAssessment = useCallback(async (latitude: number, longitude: number): Promise<void> => {
    setInternalIsLoadingRiskAssessment(true);
    
    try {
      const riskResponse = await orderfulApi.evaluateDropPointRisk({
        latitude,
        longitude,
        radius: MAP_CONFIG.RISK_ASSESSMENT_RADIUS,
        cellId: MAP_CONFIG.DEFAULT_CELL_ID,
      });
      
      // Convert API response to RiskAssessment format
      const assessment: RiskAssessment = {
        groundRisk: riskResponse.heightData?.height !== null && riskResponse.heightData?.height >= 1.0 ? 'HIGH' : 'LOW',
        buildingRisk: riskResponse.isNearBuilding ? 'HIGH' : 'LOW',
        overallRisk: (riskResponse.isNearBuilding || (riskResponse.heightData?.height !== null && riskResponse.heightData?.height >= 1.0)) ? 'HIGH' : 'LOW',
        canSave: !riskResponse.isNearBuilding || (riskResponse.heightData?.height !== null && riskResponse.heightData?.height < 1.0),
        message: riskResponse.isNearBuilding ? 'Near building detected' : 'Clear area',
        heightData: riskResponse.heightData?.height || undefined,
        buildingReason: riskResponse.isNearBuilding ? 'Near building detected' : 'Clear of buildings'
      };
      
      setInternalRiskAssessment(assessment);
    } catch (error) {
      console.error('Risk assessment failed:', error);
      
      // Create error assessment with orange circle
      const errorAssessment: RiskAssessment = {
        groundRisk: 'ERROR',
        buildingRisk: 'ERROR',
        overallRisk: 'ERROR',
        canSave: false,
        message: 'Risk evaluation failed',
        hasError: true
      };
      
      setInternalRiskAssessment(errorAssessment);
    } finally {
      setInternalIsLoadingRiskAssessment(false);
    }
  }, []);

  // Fetch obstacles when selectedCellId changes
  useEffect(() => {
    if (selectedCellId) {
      dispatch(getObstacles(selectedCellId, true));
    }
  }, [selectedCellId]);

  // Trigger risk assessment when drop point is selected OR when place context loads with preferred drop point
  useEffect(() => {
    if (placeContext) {
      const dropPoints = placeContext.dropPoints?.data || [];

      // Find the drop point to evaluate (selected or preferred on load)
      let dropPointToEvaluate = null;
      
      if (selectedDropPointId) {
        // Use selected drop point if one is selected
        dropPointToEvaluate = dropPoints.find(dp => dp.id === selectedDropPointId);
      } else if (placeContext.place.preferredDropPointId) {
        // Auto-evaluate preferred drop point on load
        dropPointToEvaluate = dropPoints.find(dp => dp.id === placeContext.place.preferredDropPointId && !dp.isDeleted);
      }
      
      if (dropPointToEvaluate) {
        // Only perform internal risk assessment if no external assessment is provided
        if (!externalRiskAssessment) {
          performRiskAssessment(dropPointToEvaluate.latitude, dropPointToEvaluate.longitude);
        }
      } else {
        if (!externalRiskAssessment) {
          setInternalRiskAssessment(null);
        }
      }
    } else {
      if (!externalRiskAssessment) {
        setInternalRiskAssessment(null);
      }
    }
  }, [selectedDropPointId, placeContext, performRiskAssessment, externalRiskAssessment]);
  // Parse and create GeoJSON for the building
  const buildingGeoJSON = useMemo(() => {
    if (!placeContext?.place?.geometry) return null;

    try {
      const geometry = typeof placeContext.place.geometry === 'string' 
        ? JSON.parse(placeContext.place.geometry)
        : placeContext.place.geometry;

      return {
        type: 'Feature' as const,
        properties: {
          id: placeContext.place.id,
          friendlyName: placeContext.place.friendlyName,
          enabled: placeContext.place.enabled,
        },
        geometry,
      };
    } catch (error) {
      console.error('Error parsing building geometry:', error);
      return null;
    }
  }, [placeContext?.place]);

  // Get drop points array
  const dropPoints = useMemo(() => {
    if (!placeContext?.dropPoints) return [];
    
    return placeContext.dropPoints?.data || [];
  }, [placeContext?.dropPoints]);

  // Building style function
  const buildingStyle = useCallback((feature: any) => {
    const isEnabled = feature?.properties?.enabled;
    return {
      fillColor: isEnabled ? '#3B82F6' : '#EF4444',
      weight: 2,
      opacity: 1,
      color: isEnabled ? '#1D4ED8' : '#DC2626',
      fillOpacity: 0.3,
    };
  }, []);

  // Building tooltip
  const onEachBuildingFeature = useCallback((feature: any, layer: any) => {
    const { friendlyName, enabled } = feature.properties;
    const status = enabled ? 'Enabled' : 'Disabled';
    
    layer.bindTooltip(
      `<strong>${friendlyName}</strong><br/>Status: ${status}`,
      { 
        permanent: false, 
        direction: 'top',
        className: 'building-tooltip'
      }
    );
  }, []);

  // Get circle colors based on risk assessment
  const getCircleColors = useCallback((assessment: RiskAssessment | null, isLoading: boolean) => {
    // Only show white circle while loading if there's no existing assessment to display
    if (isLoading && !assessment) {
      return { 
        color: RISK_COLORS.UNKNOWN, 
        fillColor: RISK_COLORS.UNKNOWN, 
        weight: 3,
        fillOpacity: 0.2 
      };
    }

    if (!assessment) {
      return { 
        color: RISK_COLORS.UNKNOWN, 
        fillColor: RISK_COLORS.UNKNOWN, 
        weight: 3,
        fillOpacity: 0.2 
      };
    }

    // Handle error state - show orange circle
    if (assessment.hasError || assessment.buildingRisk === 'ERROR' || assessment.groundRisk === 'ERROR') {
      return {
        color: RISK_COLORS.ERROR,
        fillColor: RISK_COLORS.ERROR,
        weight: 3,
        fillOpacity: 0.3
      };
    }

    // Border color based on building risk
    let strokeColor = RISK_COLORS.UNKNOWN; // Default white for no result
    if (assessment.buildingRisk === 'HIGH') {
      strokeColor = RISK_COLORS.HIGH; // Red border for near building
    } else if (assessment.buildingRisk === 'LOW') {
      strokeColor = RISK_COLORS.LOW; // Green border for clear area
    }

    // Fill color based on ground risk (height data)
    let fillColor = RISK_COLORS.UNKNOWN; // Default white for no result
    if (assessment.groundRisk === 'HIGH') {
      fillColor = RISK_COLORS.HIGH; // Red fill for unsafe (obstacles detected ≥ 1.0m)
    } else if (assessment.groundRisk === 'LOW') {
      fillColor = RISK_COLORS.LOW; // Green fill for safe (< 1.0m height)
    }

    return { 
      color: strokeColor, 
      fillColor: fillColor, 
      weight: 3,
      fillOpacity: 0.2 
    };
  }, []);

  // Circle colors for selected drop point (uses any available risk assessment)
  const selectedDropPointCircleColors = useMemo(() => 
    getCircleColors(riskAssessment, isLoadingRiskAssessment), 
    [riskAssessment, isLoadingRiskAssessment, getCircleColors]
  );

  // Circle colors for new pin (uses external risk assessment from parent)
  const newPinCircleColors = useMemo(() => 
    getCircleColors(externalRiskAssessment, externalIsLoadingRiskAssessment), 
    [externalRiskAssessment, externalIsLoadingRiskAssessment, getCircleColors]
  );

  // Create drop point markers using the same styling as DropPointManagement
  const dropPointMarkers = useMemo(() => {
    return dropPoints
      .filter(dp => showDeletedPoints || !dp.isDeleted)
      .map(dropPoint => {
        const isSelected = dropPoint.id === selectedDropPointId;
        const isPreferred = dropPoint.id === placeContext?.place?.preferredDropPointId;
        const isVerified = dropPoint.verified;
        const isDeleted = dropPoint.isDeleted;
        
        const customIcon = createCachedDropPointIcon(isSelected, isDeleted, isVerified, isPreferred);
        // Ensure preferred pins render above all others
        const zIndexOffset = (isPreferred ? 10000 : 0) + (isSelected ? 1000 : 0) + (isDeleted ? -1000 : 0);

        return (
          <Marker
            key={dropPoint.id}
            position={[dropPoint.latitude, dropPoint.longitude]}
            icon={customIcon}
            zIndexOffset={zIndexOffset}
            eventHandlers={{
              click: () => {
                onDropPointSelect(dropPoint.id, dropPoint.latitude, dropPoint.longitude);
              }
            }}
          />
        );
      });
  }, [dropPoints, selectedDropPointId, placeContext?.place?.preferredDropPointId, onDropPointSelect, createCachedDropPointIcon, showDeletedPoints]);

  // Get selected drop point for risk assessment circle
  const selectedDropPoint = useMemo(() => {
    if (!selectedDropPointId || !dropPoints) return null;
    return dropPoints.find(dp => dp.id === selectedDropPointId) || null;
  }, [selectedDropPointId, dropPoints]);

  // Default center (Dublin coordinates as fallback)
  const defaultCenter: [number, number] = [53.3498, -6.2603];
  const mapCenter = placeContext?.place?.centroid 
    ? [placeContext.place.centroid.latitude, placeContext.place.centroid.longitude] as [number, number]
    : defaultCenter;

  if (!placeContext) {
    return (
      <div className={`bg-gray-100 rounded-lg flex items-center justify-center ${className}`} style={{ height }}>
        <p className="text-gray-500">Enter an Eircode to view the map</p>
      </div>
    );
  }

  return (
    <div className={`rounded-lg overflow-hidden border border-gray-200 ${className}`} style={{ height }}>
      <MapContainer
        center={mapCenter}
        zoom={17}
        style={{ height: '100%', width: '100%', zIndex: 0 }}
        className="rounded-lg"
      >
        <TileLayer
          attribution="Manna-v1"
          url="https://tiles.manna.aero/{z}/{x}/{y}.png"
          maxZoom={24}
        />
        
        {/* Building GeoJSON */}
        {buildingGeoJSON && (
          <GeoJSON
            key={`building-${placeContext.place.id}-${placeContext.place.enabled}`}
            data={buildingGeoJSON}
            style={buildingStyle}
            onEachFeature={onEachBuildingFeature}
          />
        )}

        {/* Obstacles */}
        <Obstacles obstacles={obstacles} />

        {/* Drop Point Markers */}
        {dropPointMarkers}

        {/* New Pin Marker for placement mode */}
        {newPinLocation && clickMode === 'add-drop-point' && (
          <Marker
            position={[newPinLocation.lat, newPinLocation.lng]}
            icon={createNewPinIcon()}
            draggable={true}
            eventHandlers={{
              dragend: (e) => {
                const marker = e.target;
                const position = marker.getLatLng();
                onPinMove?.(position);
              }
            }}
          />
        )}

        {/* Suggested Drop Point Marker */}
        {suggestedDropPoint && (
          <Marker
            position={[suggestedDropPoint[0], suggestedDropPoint[1]]}
            icon={createSuggestedPinIcon()}
            draggable={false}
          >
            <Tooltip permanent direction="bottom" opacity={1} offset={[0, 10]}>
              {'Requested'}
            </Tooltip>
          </Marker>
        )}

        {/* Risk Assessment Circle for selected drop point */}
        {selectedDropPoint && (riskAssessment || internalRiskAssessment) && (
          <Circle
            key={`selected-circle-${selectedDropPoint.id}-${JSON.stringify(selectedDropPointCircleColors)}`}
            center={[selectedDropPoint.latitude, selectedDropPoint.longitude]}
            radius={MAP_CONFIG.RISK_ASSESSMENT_RADIUS} // 3 meter radius for assessment
            {...selectedDropPointCircleColors}
            opacity={0.8}
          />
        )}
        
        {/* Risk Assessment Circle for new pin */}
        {newPinLocation && clickMode === 'add-drop-point' && (externalRiskAssessment || externalIsLoadingRiskAssessment) && (
          <Circle
            key={`new-pin-circle-${newPinLocation.lat}-${newPinLocation.lng}-${JSON.stringify(newPinCircleColors)}`}
            center={[newPinLocation.lat, newPinLocation.lng]}
            radius={MAP_CONFIG.RISK_ASSESSMENT_RADIUS} // 3 meter radius for assessment
            {...newPinCircleColors}
            opacity={0.8}
          />
        )}
        
        {/* Map click handler */}
        <MapClickHandler clickMode={clickMode} onPinMove={onPinMove} />
        
        {/* Map bounds controller */}
        <MapBoundsController placeContext={placeContext} />
      </MapContainer>
      
      {/* Legend */}
      {showLegend && (
        <div className="absolute bottom-4 left-4 bg-white p-3 rounded-lg shadow-lg border text-xs z-[1000]">
          <div className="space-y-1">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded-full border-2 border-white shadow" style={{ backgroundColor: MARKER_STYLES.UNVERIFIED_COLOR }}></div>
              <span>Unverified</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded-full border-2 border-white shadow" style={{ backgroundColor: MARKER_STYLES.VERIFIED_COLOR }}></div>
              <span>Verified</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded-full border-2 border-yellow-400 shadow flex items-center justify-center text-white text-xs" style={{ backgroundColor: MARKER_STYLES.VERIFIED_COLOR }}>★</div>
              <span>Preferred</span>
            </div>
            {showDeletedPoints && (
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 rounded-full border-2 border-white shadow opacity-60" style={{ backgroundColor: MARKER_STYLES.DELETED_COLOR }}></div>
                <span>Deleted</span>
              </div>
            )}
            <div className="flex items-center space-x-2">
              <div className="w-4 h-2 bg-blue-500 opacity-30 border border-blue-700"></div>
              <span>Building</span>
            </div>
            {selectedDropPoint && (riskAssessment || internalRiskAssessment) && (
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 rounded-full border-2" style={{ borderColor: selectedDropPointCircleColors.color, backgroundColor: selectedDropPointCircleColors.fillColor }}></div>
                <span>
                  {riskAssessment?.hasError || internalRiskAssessment?.hasError ? 'Eval Error' : 'Risk Zone'}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PlaceDropPointMap;
